import type { Language } from '@aviv-unified-frontend/runtime/i18n';

import type {
    IAutocompleteParams,
    IGeoMatchItem,
    IGeoMatchPlace,
    IGeoPlace,
    IGeocodingParams,
} from '@/frontend/src/types/geo';
import {
    EGeoCountry,
    EOrdinalExtension,
    EPlaceTypes,
} from '@/frontend/src/types/geo';
import { EPortal } from '@/frontend/src/types/portal';
import {
    autocompleteApiRequest,
    geocodingApiRequest,
} from '@/frontend/src/services/geoApiRequest';
import type { IGeoProps } from '@/frontend/src/types/context';

const getDefaultGeoCountry = (
    portal: EPortal | undefined,
): EGeoCountry | undefined => {
    switch (portal) {
        case EPortal.Immonet:
        case EPortal.Immowelt: {
            return EGeoCountry.Germany;
        }
        case EPortal.Immoweb: {
            return EGeoCountry.Belgium;
        }
        case EPortal.SeLoger:
        case EPortal.LogicImmo: {
            return EGeoCountry.France;
        }
        default: {
            throw new Error('Unhandled portal');
        }
    }
};

export const getCountries = (
    geo: IGeoProps,
    portal: EPortal,
): EGeoCountry[] => {
    if (geo.countries?.length) {
        return geo.countries;
    }

    const defaultCountry = getDefaultGeoCountry(portal);

    return defaultCountry ? [defaultCountry] : [];
};

export const extractItemLabels = (
    item: IGeoPlace | undefined,
    locale: Language,
) => {
    if (item) {
        const names = item.names[locale]
            ? item.names[locale]
            : item.names[item.language];

        return names?.[0];
    }

    return undefined;
};

export const extractAutocompleteData = (
    autocompleteResponse: IGeoPlace[],
    locale: Language,
) => {
    return autocompleteResponse.map((item) => {
        const municipalityItem = item.parents?.find(
            (parent: IGeoPlace) => parent.type_key === EPlaceTypes.Municipality,
        );

        const boroughItem = item.parents?.find(
            (parent: IGeoPlace) => parent.type_key === EPlaceTypes.Borough,
        );

        const streetItem = item.parents?.find(
            (parent: IGeoPlace) => parent.type_key === EPlaceTypes.Street,
        );

        const locationLabels = extractItemLabels(item, locale);
        const municipalityLabels = extractItemLabels(municipalityItem, locale);
        const boroughLabels = extractItemLabels(boroughItem, locale);
        const streetLabels = extractItemLabels(streetItem, locale);
        let street = '';
        let houseNumber = '';
        const typeKey = item.type_key;

        if (typeKey === EPlaceTypes.Street) {
            street = locationLabels?.name ?? '';
        }

        if (typeKey === EPlaceTypes.HouseNumber) {
            street = streetLabels?.name ?? '';
            houseNumber = locationLabels?.name ?? '';
        }

        return {
            id: item.id,
            name: locationLabels?.name ?? '',
            label: locationLabels?.display_name ?? '',
            postalCode: item.main_postal_code,
            typeKey,
            city: municipalityLabels?.name ?? boroughLabels?.name ?? '',
            street,
            houseNumber,
        };
    });
};

export const fetchAddresses = async (
    params: IAutocompleteParams,
    locale: Language,
    portal: EPortal,
) => {
    const { items } = await autocompleteApiRequest(params, portal);

    return extractAutocompleteData(items, locale);
};

export const fetchHouseNumber = async (
    params: IGeocodingParams,
    streetId: string,
) => {
    const { items } = await geocodingApiRequest(params);

    const houseNumberGeoMatchPlace = items.aviv?.find(
        (item) =>
            item.match.type_key === EPlaceTypes.HouseNumber &&
            item.match.id &&
            item.match.confidence_score === 1, // filter out low confidence scores
    );

    if (houseNumberGeoMatchPlace) {
        const streetParent = houseNumberGeoMatchPlace.match.parents.find(
            (item) => item.type_key === EPlaceTypes.Street,
        );

        if (streetParent?.id === streetId) {
            return houseNumberGeoMatchPlace;
        }
    }

    return undefined;
};

export const getHouseNumber = (result: IGeoMatchItem) => {
    const { no_ext, no } = result.provider_result;

    if (no_ext && no) {
        // extensions are letters (eg. 12B) or ordinal extensions (eg. 12 bis)
        const isOrdinaleExtension = Object.values(EOrdinalExtension).includes(
            no_ext as EOrdinalExtension,
        );

        if (isOrdinaleExtension) {
            return `${no} ${no_ext}`;
        }

        return `${no}${no_ext}`;
    }

    if (typeof no === 'number') return `${no}`;

    return '';
};

export const getStreet = (result: IGeoMatchItem) => {
    const streetParent = result.match.parents.find(
        (item) => item.type_key === EPlaceTypes.Street,
    );

    return streetParent?.match_name ?? '';
};

export const getPostalCode = (
    result: IGeoMatchItem,
    params: IGeocodingParams,
) => {
    const zipParent = result.match.parents.find(
        (item) => item.type_key === EPlaceTypes.PostalCode,
    );

    let postalCode;

    if (zipParent?.match_name) postalCode = zipParent.match_name;

    if (!postalCode && result.provider_result.postal_code)
        postalCode = result.provider_result.postal_code;

    if (!postalCode && result.provider_result._source?.data.zips.length) {
        const zips = result.provider_result._source.data.zips;

        postalCode = zips.includes(params.postal_code)
            ? params.postal_code
            : zips[0];
    }

    return postalCode ?? '';
};

export const getCity = (result: IGeoMatchItem) => {
    /* Be aware that in the geo api responses :
    In Belgium, the municipality type can be a district of a larger city. Districts are commonly used there.
    (For instance, "Ixelles" as municipality, it's a district of Brussels). However, in France and Germany, the municipality
    type refers to cities. Districts are not typically used there in postal addresses.
    (For instance, "Paris" or "München" as municipality, rather than "Paris 1er arrondissement" or "Schwanthalerhöhe") */

    const getBorough = (parents: IGeoMatchPlace[]) => {
        const borough = parents.find(
            (item) => item.type_key === EPlaceTypes.Borough,
        );

        return borough?.match_name;
    };

    const getMunicipality = (parents: IGeoMatchPlace[]) => {
        const municipality = parents.find(
            (item) => item.type_key === EPlaceTypes.Municipality,
        );

        return municipality?.match_name;
    };

    return (
        getMunicipality(result.match.parents) ??
        getBorough(result.match.parents) ??
        ''
    );
};

export const fetchClosestLocation = async (params: IGeocodingParams) => {
    const { items } = await geocodingApiRequest(params);
    // Let's select the highest confidence score result
    const result = items.aviv?.[0];

    if (result?.match) {
        const closestLocation = {
            id: result.match.id,
            typeKey: result.match.type_key,
            postalCode: getPostalCode(result, params),
            city: getCity(result),
        };

        if (result.match.type_key === EPlaceTypes.HouseNumber) {
            return {
                ...closestLocation,
                houseNumber: getHouseNumber(result),
                street: getStreet(result),
                displayName: result.match.match_name,
            };
        }

        // In case the result is a street, we need to extract information to display the address correctly
        if (result.match.type_key === EPlaceTypes.Street) {
            return {
                ...closestLocation,
                houseNumber: '',
                street: result.match.match_name,
                displayName:
                    result.match.match_name +
                    ', ' +
                    (closestLocation.postalCode
                        ? closestLocation.postalCode + ' '
                        : '') +
                    closestLocation.city,
            };
        }
    }

    return undefined;
};
